import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import AddressAutocomplete from "./AddressAutocomplete";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Modal for a franchise owner to add a new store under their account.
 * Reuses the same address picker and field set as ShopAuth's individual
 * register flow, minus email/password (inherited from owner). On success
 * the parent refreshes its list; the owner can immediately add another
 * store or close to view the dashboard.
 */
export default function FranchiseAddStoreDialog({ open, onOpenChange, token, onCreated, canCancel = true }) {
  const [data, setData] = useState({
    shop_name: "",
    contact_name: "",
    phone: "",
    address: null,
    google_maps_link: "",
    description: "",
    service_radius_km: 50,
  });
  const [submitting, setSubmitting] = useState(false);

  const reset = () => setData({
    shop_name: "",
    contact_name: "",
    phone: "",
    address: null,
    google_maps_link: "",
    description: "",
    service_radius_km: 50,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.shop_name?.trim()) return toast.error("Store name is required");
    if (!data.address?.full_address) return toast.error("Store address is required");
    if (!data.phone?.trim()) return toast.error("Phone is required");
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/franchise/stores`,
        {
          shop_name: data.shop_name,
          contact_name: data.contact_name || null,
          address: data.address,
          phone: data.phone,
          google_maps_link: data.google_maps_link || null,
          description: data.description || null,
          service_radius_km: parseInt(data.service_radius_km, 10) || 50,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${data.shop_name} added — pending admin approval`);
      reset();
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add store");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={canCancel ? onOpenChange : undefined}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="add-store-dialog"
        onPointerDownOutside={canCancel ? undefined : (e) => e.preventDefault()}
        onEscapeKeyDown={canCancel ? undefined : (e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Add a Store</DialogTitle>
          <DialogDescription>
            Add a new branch under your multi-location account. Each store is
            reviewed for approval before it can receive quote requests.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3" data-testid="add-store-form">
          <div className="space-y-1">
            <Label htmlFor="store-name">Store / Branch Name *</Label>
            <Input
              id="store-name"
              value={data.shop_name}
              onChange={(e) => setData({ ...data, shop_name: e.target.value })}
              required
              data-testid="store-name-input"
              placeholder="e.g. Bridgestone Albany"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="store-contact">Branch Contact Name</Label>
            <Input
              id="store-contact"
              value={data.contact_name}
              onChange={(e) => setData({ ...data, contact_name: e.target.value })}
              data-testid="store-contact-input"
              placeholder="Branch manager (optional)"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="store-address">Store Address *</Label>
            <AddressAutocomplete
              value={data.address}
              onChange={(address) => setData({ ...data, address })}
              placeholder="Start typing the store address..."
              required
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="store-phone">Phone *</Label>
            <Input
              id="store-phone"
              type="tel"
              value={data.phone}
              onChange={(e) => setData({ ...data, phone: e.target.value })}
              required
              data-testid="store-phone-input"
              placeholder="+64 9 123 4567"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="store-radius">Service radius (km)</Label>
              <Input
                id="store-radius"
                type="number"
                min="1"
                max="100"
                value={data.service_radius_km}
                onChange={(e) => setData({ ...data, service_radius_km: e.target.value })}
                data-testid="store-radius-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="store-maps">Google Maps link (optional)</Label>
              <Input
                id="store-maps"
                value={data.google_maps_link}
                onChange={(e) => setData({ ...data, google_maps_link: e.target.value })}
                data-testid="store-maps-input"
                placeholder="https://maps.google.com/..."
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="store-desc">Description (optional)</Label>
            <Textarea
              id="store-desc"
              rows={2}
              value={data.description}
              onChange={(e) => setData({ ...data, description: e.target.value })}
              data-testid="store-desc-input"
            />
          </div>

          <div className="flex flex-wrap justify-end gap-2 pt-2">
            {canCancel && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => { reset(); onOpenChange(false); }}
                disabled={submitting}
                data-testid="cancel-add-store-btn"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-add-store-btn"
            >
              {submitting ? "Adding..." : "Add Store"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
